/**
 * Domain model and pure helper functions for HIS V4 Screening screen (คัดกรองผู้ป่วย)
 * Covers: 1. อาการนำ (Triage), 2. ประวัติสุขภาพ (Health Profile / ADR), 3. Vital Sign & Neurological Sign, 3.1 Body Composition
 */

export const asNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === "") return undefined;
  const num = Number(value);
  return Number.isFinite(num) ? num : undefined;
};

// ============================================================================
// 1. Triage (อาการนำและระดับคัดกรอง)
// ============================================================================

export interface TriageLevelDef {
  level: string; // "1" | "2" | "3" | "4" | "5" ตาม Triage.TRIAGE_LEVEL_CHOICES
  /** ชื่อระดับตามที่แสดงต่อท้ายปุ่ม (issue 86614) */
  name: string;
  /** คำอธิบายยาวใช้กับ title/tooltip */
  desc: string;
  /** สีพื้นของปุ่มเมื่อถูกเลือก ต้องเท่ากับ token $hv4-triage-n ใน _hisv4-theme.scss */
  color: string;
  /** สีตัวอักษรบนพื้น color (ระดับ 3 และ 4 ใช้ตัวอักษรหม่นเข้ม) */
  ink: string;
  bg: string;
}

/* issue 86614 ตารางสีระดับคัดกรอง (1 ช่วยชีวิต ... 5 ทั่วไป) - ชุดเดียวกับ badge ในหน้าคิว */
export const TRIAGE_LEVELS: TriageLevelDef[] = [
  { level: "1", name: "ช่วยชีวิต", desc: "Resuscitation - ต้องช่วยชีวิตทันที", color: "#D9362B", ink: "#FFFFFF", bg: "#FCE8E6" },
  { level: "2", name: "ฉุกเฉิน", desc: "Emergency - ต้องตรวจภายใน 10 นาที", color: "#E8761B", ink: "#FFFFFF", bg: "#FEF7E0" },
  { level: "3", name: "เร่งด่วน", desc: "Urgent - ควรตรวจภายใน 30 นาที", color: "#F2B01E", ink: "#3B2A00", bg: "#FFF0D4" },
  { level: "4", name: "ไม่รุนแรง", desc: "Semi-urgent - ตรวจภายใน 60 นาที", color: "#5BAF4A", ink: "#10300A", bg: "#E6F4EA" },
  { level: "5", name: "ทั่วไป", desc: "Non-urgent - รอตรวจตามลำดับ", color: "#2E9E4F", ink: "#FFFFFF", bg: "#E8F0FE" },
];

export interface TriageData {
  id?: number;
  chiefComplaint: string;
  triageLevel?: string; // "1" - "5" ตาม Triage.TRIAGE_LEVEL_CHOICES
  arriveStatus?: string | number;
  arriveStatusLabel?: string;
  /** id ของ ClinicalTerm (type "PAB") ที่ backend ต้องใช้ใน Triage.arrive_status */
  arriveStatusId?: number;
  /** id ของ ClinicalTerm (type "PC") ที่ backend ต้องใช้ใน Triage.case */
  caseId?: number;
  /** Triage.patient_type (backend default "WLK"; V3 ส่งค่าจาก combo ผู้ป่วยเดิน/มาเอง, APP = ผู้ป่วยนัด) */
  patientType?: string;
  /** Triage.referer - model เป็น CharField(null=False, blank=True) จึงต้องส่งเสมอ (ส่ง "" ได้) */
  referer?: string;
}

/**
 * pre-check ก่อนสร้างการคัดกรอง (issue 86747 ต่อ)
 *
 * backend (TriageSerializer.validate_patient_id) ไม่ได้ผูก Triage กับ encounter ที่กำลังเปิดอยู่
 * แต่ lookup `Encounter.objects.filter_unexpired().filter(patient=<patient_id>, status=WAIT_TRIAGE)`
 * (โรงพยาบาล non-cu) แล้วผูก/ตั้ง action OPD_TRIAGE กับ encounter ที่พบทั้งหมด
 *
 * สัญญาณที่可信ที่สุดคือ `allowed_actions` จาก GET /apis/core/encounter/<id>/ ซึ่ง backend คำนวณจาก
 * Encounter.TRANSITION จริง (ตรวจกับ API จริง: 9838 WAIT_TRIAGE -> มี OPD_TRIAGE, 9843 IN_QUEUE -> ไม่มี,
 * 9837 ARRIVING -> ไม่มี แต่มี triage เดิม (id 2906) ซึ่งแก้ได้ผ่าน PATCH /apis/core/triage/<id>/)
 */
export interface TriageEligibility {
  encounterId?: number;
  status?: number;
  statusName?: string;
  /** สร้างการคัดกรองใหม่ได้หรือไม่ (ตาม allowed_actions ของ Encounter) */
  allowsCreate: boolean;
  /** มีแถว Triage ของ encounter นี้อยู่แล้ว -> บันทึกได้ผ่าน PATCH (ไม่ติด validate_patient_id) */
  hasExistingTriage: boolean;
  /** backend ไม่ส่ง allowed_actions มา (โหลดไม่ครบ) -> ห้าม block ให้ลองบันทึกแล้ว map error แทน */
  known: boolean;
}

export const triageEligibilityOf = (
  encounterRow: unknown,
  hasExistingTriage: boolean,
  encounterId?: number,
): TriageEligibility => {
  const row = (encounterRow ?? null) as {
    allowed_actions?: unknown;
    status?: unknown;
    status_name?: unknown;
  } | null;
  const allowed = Array.isArray(row?.allowed_actions) ? (row?.allowed_actions as unknown[]).map(String) : null;
  const status = asNumber(row?.status);
  const statusName = String(row?.status_name ?? "").trim();

  return {
    encounterId,
    status,
    statusName,
    hasExistingTriage,
    known: allowed !== null,
    allowsCreate: hasExistingTriage ? true : allowed === null ? true : allowed.indexOf("OPD_TRIAGE") >= 0,
  };
};

/** ข้อความอธิบายผู้ใช้เมื่อ pre-check ห้ามสร้างการคัดกรองใหม่ */
export const triageBlockedMessageOf = (elig: TriageEligibility): string =>
  "บันทึกอาการนำไม่ได้: Encounter #" +
  `${elig.encounterId ?? "-"} อยู่สถานะ "${elig.statusName || "-"}" ซึ่ง backend ไม่อนุญาตให้สร้างการคัดกรองใหม่ ` +
  "(ต้องเป็น encounter สถานะ \"รอคัดกรอง\" (WAIT_TRIAGE) ที่ยังไม่หมดอายุของผู้ป่วยนั้น เพราะระบบผูกการคัดกรองกับ encounter ตาม HN)" +
  " - กรุณาเปิดหน้าคัดกรองของ encounter ที่รอคัดกรอง หรือบันทึกอาการนำของการคัดกรองเดิม";

/**
 * แปลง error จาก TriageSerializer (POST /apis/core/triage/) ให้เป็นข้อความที่ผู้ใช้เข้าใจ (#86747)
 * - ขาด patient_id -> DRF ตอบ {"patient_id": ["ฟิลด์นี้จำเป็น"]}
 * - validate_patient_id ตอบ 'Patient = X doesn't have any waiting triage encounter'
 *   เพราะ backend มองหา encounter สถานะ WAIT_TRIAGE ของผู้ป่วย (โรงพยาบาล non-cu) แล้วผูก triage กับ encounter นั้น
 *   ไม่ใช่ encounter ที่กำลังเปิดคัดกรองอยู่
 */
export const friendlyTriageErrorText = (text?: string | null): string => {
  const raw = String(text ?? "").trim();
  if (raw === "") return "บันทึกการคัดกรองไม่สำเร็จ";
  if (/waiting triage encounter/i.test(raw)) {
    return (
      "บันทึกการคัดกรองไม่ได้: ผู้ป่วยนี้ไม่มี encounter สถานะ รอคัดกรอง (WAIT_TRIAGE) ที่ยังไม่หมดอายุ " +
      "- backend ผูก triage กับ encounter สถานะนั้นตาม HN ไม่ใช่ encounter ที่กำลังเปิดอยู่"
    );
  }
  if (/patient_id/i.test(raw) && /(จำเป็น|required)/i.test(raw)) {
    return "บันทึกการคัดกรองไม่สำเร็จ: backend ต้องใช้ patient_id ของผู้ป่วย";
  }
  return raw;
};

// ============================================================================
// 2. Health Profile & Allergy (ประวัติสุขภาพประจำตัว และประวัติแพ้)
// ============================================================================

export interface AllergyItem {
  id?: number;
  type: "ยา" | "อาหาร" | "สารเคมี" | "อื่น ๆ";
  name: string;
  react: string;
}

export interface HealthProfileData {
  underlyingDiseases: string; // โรคประจำตัว
  surgicalHistory: string; // ประวัติการผ่าตัด
  currentMedications: string; // ยาที่ใช้ประจำ
  smoking: string; // สูบบุหรี่ (ไม่สูบ / สูบเป็นครั้งคราว / สูบประจำ)
  alcohol: string; // ดื่มสุรา (ไม่ดื่ม / ดื่มเป็นครั้งคราว / ดื่มประจำ)
  nkda: boolean; // ปฏิเสธการแพ้ทุกประเภท (No Known Drug Allergies)
  allergies: AllergyItem[];
  recordedBy?: string;
  recordedAt?: string;
  /** จริงเมื่อ created == edited (ยังไม่เคยถูกแก้) -> แสดง "บันทึกครั้งแรกโดย" */
  firstSaved?: boolean;
  /** id ของแถว AdverseReaction ที่เป็น "ปฏิเสธการแพ้" (NO_REACTION) จริงบน backend
      ใช้ยกเลิกเมื่อกดถอดติ๊ก NKDA (backend ไม่ยอม DELETE) */
  nkdaRecordId?: number;
}

/* ============================================================== */
/*   ประวัติสุขภาพประจำตัว (issue 86615) - เก็บตาม HN             */
/* ============================================================== */

/**
 * backend ไม่มี field สำหรับ "ยาที่ใช้ประจำ" "ประวัติผ่าตัด" "สูบบุหรี่" "ดื่มสุรา" แบบ patient-scoped
 * (Encounter.regularly_taken_supplements เป็น M2M ไป ClinicalTerm master และ
 *  ProblemList เป็น table เดียวที่เป็น patient-scoped + มี edited สำหรับ audit)
 * จึงเก็บลง ProblemList โดยขึ้นต้น message ด้วยหมวดหมู่ ภาษาไทย
 * - row ที่ไม่มีคำขึ้นต้น (ของเดิมที่ V3 เขียนไว้) ถือว่าเป็น "โรคประจำตัว"
 */
export const PROFILE_PROBLEM_PREFIX = {
  disease: "โรคประจำตัว:",
  medication: "ยาที่ใช้ประจำ:",
  surgery: "ประวัติผ่าตัด:",
  smoking: "สูบบุหรี่:",
  alcohol: "ดื่มสุรา:",
} as const;

export type ProfileProblemCategory = keyof typeof PROFILE_PROBLEM_PREFIX;

export interface ProblemListRow {
  id: number;
  message: string;
  is_active: boolean;
  edited?: string;
  created?: string;
  edit_user?: string | null;
  edit_user_name?: string | null;
}

const categoryOfMessage = (message: string): ProfileProblemCategory => {
  const text = String(message || "").trim();
  if (text.startsWith(PROFILE_PROBLEM_PREFIX.medication)) return "medication";
  if (text.startsWith(PROFILE_PROBLEM_PREFIX.surgery)) return "surgery";
  if (text.startsWith(PROFILE_PROBLEM_PREFIX.smoking)) return "smoking";
  if (text.startsWith(PROFILE_PROBLEM_PREFIX.alcohol)) return "alcohol";
  return "disease";
};

const valueOfMessage = (message: string): string => {
  const text = String(message || "").trim();
  const withoutPrefix = text.replace(/^(โรคประจำตัว|ยาที่ใช้ประจำ|ประวัติผ่าตัด|สูบบุหรี่|ดื่มสุรา):\s*/, "");
  return withoutPrefix.trim();
};

/* เปิดให้ Patient Panel จำแนกแถวของ ProblemList ได้โดยไม่ต้อง hardcode คำขึ้นต้นซ้ำ */
export const problemCategoryOf = categoryOfMessage;
export const problemValueOf = valueOfMessage;

/** คำที่ขึ้นต้นด้วย "ไม่มี" ถือว่าไม่มีข้อมูล (issue 86615 ข้อ 8) */
export const hasProfileValue = (text: unknown): boolean => {
  const value = String(text ?? "").trim();
  if (!value) return false;
  if (/^ไม่มี/.test(value)) return false;
  if (/^ไม่(สูบ|ดื่ม|ระบุ)/.test(value)) return false;
  return true;
};

export interface SplitProfileProblems {
  underlyingDiseases: string;
  currentMedications: string;
  surgicalHistory: string;
  /** ว่างเมื่อ "ยังไม่เคยบันทึกตาม HN" (ค่า default ของ UI คือ ไม่สูบ / ไม่ดื่ม) */
  smoking: string;
  alcohol: string;
  rows: Record<ProfileProblemCategory, ProblemListRow[]>;
}

/** อ่าน ProblemList (patient-scoped) มาจำแนกเป็นช่องของประวัติสุขภาพประจำตัว */
export const splitProfileProblems = (rows: ProblemListRow[]): SplitProfileProblems => {
  const grouped: Record<ProfileProblemCategory, ProblemListRow[]> = {
    disease: [],
    medication: [],
    surgery: [],
    smoking: [],
    alcohol: [],
  };

  rows
    .filter((row) => row && row.is_active !== false)
    .forEach((row) => grouped[categoryOfMessage(row.message)].push(row));

  const join = (list: ProblemListRow[]): string =>
    list.map((row) => valueOfMessage(row.message)).filter(Boolean).join(", ");

  return {
    underlyingDiseases: join(grouped.disease),
    currentMedications: join(grouped.medication),
    surgicalHistory: join(grouped.surgery),
    /* สูบบุหรี่ / ดื่มสุรา เก็บเป็นแถวเดียวต่อหมวด (ค่าทั้งช่อง ไม่ split ด้วยจุลภาค) */
    smoking: join(grouped.smoking),
    alcohol: join(grouped.alcohol),
    rows: grouped,
  };
};

export interface ProblemPlan {
  updates: { id: number; message: string }[];
  creates: { message: string }[];
  deactivates: { id: number }[];
}

/**
 * เปรียบเทียบค่าในการ์ดกับ row ที่เก็บไว้ -> ได้คำสั่งเท่าที่จำเป็น
 * (งานเดิมของ AI สร้าง row ใหม่ซ้ำทุกครั้งที่กดบันทึก)
 */
export const problemPlanOf = (
  rows: Record<ProfileProblemCategory, ProblemListRow[]>,
  profile: { underlyingDiseases: string; currentMedications: string; surgicalHistory: string; smoking?: string; alcohol?: string },
): ProblemPlan => {
  const plan: ProblemPlan = { updates: [], creates: [], deactivates: [] };
  const wanted: Record<ProfileProblemCategory, string[]> = {
    disease: splitValues(profile.underlyingDiseases, profile, "disease"),
    medication: splitValues(profile.currentMedications, profile, "medication"),
    surgery: splitValues(profile.surgicalHistory, profile, "surgery"),
    smoking: wholeValueOf(profile.smoking),
    alcohol: wholeValueOf(profile.alcohol),
  };

  (Object.keys(wanted) as ProfileProblemCategory[]).forEach((category) => {
    const prefix = PROFILE_PROBLEM_PREFIX[category];
    const existing = rows[category] || [];
    const desired = wanted[category];

    /* ใช้ row เดิมเท่าที่จำเป็น เรียงตาม id เพื่อให้อ่านง่ายและไม่นับซ้ำ */
    const sortedExisting = [...existing].sort((a, b) => a.id - b.id);
    desired.forEach((value, index) => {
      const message = `${prefix} ${value}`;
      const row = sortedExisting[index];
      if (row) {
        if (String(row.message).trim() !== message) plan.updates.push({ id: row.id, message });
      } else {
        plan.creates.push({ message });
      }
    });

    /* row ที่เหลือเกินจากค่าปัจจุบัน -> is_active: false (ProblemListDetail ไม่อนุญาต DELETE) */
    sortedExisting.slice(desired.length).forEach((row) => plan.deactivates.push({ id: row.id }));
  });

  return plan;
};

const splitValues = (
  text: string,
  profile: { underlyingDiseases: string; currentMedications: string; surgicalHistory: string },
  category: ProfileProblemCategory,
): string[] => {
  const raw = String(text || "").trim();
  if (!hasProfileValue(raw)) return [];
  const separator = category === "medication" ? /\r?\n|,/ : /[,;\n]/;
  return raw
    .split(separator)
    .map((part) => part.trim())
    .filter((part) => part !== "" && !/^ไม่มี$/.test(part));
};

/**
 * ช่อง "สูบบุหรี่" / "ดื่มสุรา" เก็บเป็น แถวเดียวต่อหมวด
 * (ค่าอย่าง "ดื่มเป็นครั้งคราว (สังสรรค์)" ต้องไม่ถูก split เป็น 2 แถว)
 * และค่า "ไม่สูบ" / "ไม่ดื่ม" = ไม่มีข้อมูล (ข้อ 8) -> ไม่ได้สร้างแถว
 */
const wholeValueOf = (text: unknown): string[] => {
  const raw = String(text ?? "").trim();
  return hasProfileValue(raw) ? [raw] : [];
};

/** "บันทึกครั้งแรกโดย {ชื่อ} · {วันเวลา}" หรือ "บันทึกล่าสุดโดย ..." (issue 86615 ข้อ 3) */
/**
 * แถวของ ProblemList ที่หน้าคัดกรองจะบันทึก (ใช้โดย Patient Panel เพื่อ_show flag ให้ตรงกัน
 * ระหว่าง "เพิ่งบันทึกในหน้าคัดกรอง" กับข้อมูลที่ parent โหลดไว้ก่อนแล้ว)
 */
export const profileProblemMessagesOf = (profile?: Partial<HealthProfileData> | null): string[] => {
  if (!profile) return [];
  const entries: [ProfileProblemCategory, string][] = [
    ["disease", String(profile.underlyingDiseases ?? "")],
    ["medication", String(profile.currentMedications ?? "")],
    ["surgery", String(profile.surgicalHistory ?? "")],
  ];

  return entries
    .filter(([, text]) => hasProfileValue(text))
    .map(([category, text]) => `${PROFILE_PROBLEM_PREFIX[category]} ${text.trim()}`);
};

/**
 * ครบตาม issue 86615 ข้อ 5-6-7 หรือไม่
 * (3 ช่องบังคับต้องมีข้อความ - พิมพ์ "ไม่มี" ได, และต้องเลือกอย่างใดอย่างหนึ่ง: NKDA หรือมีรายการแพ้)
 */
export const isHealthProfileComplete = (profile?: Partial<HealthProfileData> | null): boolean => {
  if (!profile) return false;
  const required = [profile.underlyingDiseases, profile.currentMedications, profile.surgicalHistory];
  if (required.some((text) => String(text ?? "").trim() === "")) return false;
  return Boolean(profile.nkda) || (profile.allergies || []).length > 0;
};

/** มี "ประวัติสุขภาพที่ถูกบันทึกไว้จริง" หรือไม่ (ใช้ตัดสินติ๊กถูกของ chip "2 · ประวัติสุขภาพ")
    - นับเฉพาะค่าที่มีความหมาย (ไม่มี/ไม่สูบ/ไม่ดื่ม/ไม่ระบุ ไม่นับ) ตามข้อ 8 ของ issue 86615
    - นับแถว AdverseReaction ที่มีชื่อจริง (แถวที่ชื่อ "ไม่มี" ซึ่งเป็นของเก่าจาก V3 ไม่นับ)
    - หรือนับ audit ที่มาจาก ProblemList/AdverseReaction ที่มีผู้บันทึก */
export const hasSavedHealthProfile = (profile?: Partial<HealthProfileData> | null): boolean => {
  if (!profile) return false;
  if (profile.nkda || profile.nkdaRecordId) return true;
  if ((profile.allergies || []).some((a) => hasProfileValue((a || {}).name))) return true;
  if (
    [profile.underlyingDiseases, profile.currentMedications, profile.surgicalHistory].some((text) =>
      hasProfileValue(text),
    )
  ) {
    return true;
  }
  if (hasProfileValue(profile.smoking) && profile.smoking !== "ไม่สูบ") return true;
  if (hasProfileValue(profile.alcohol) && profile.alcohol !== "ไม่ดื่ม") return true;
  return Boolean(profile.recordedBy);
};

export interface ProfileAudit {
  recordedBy: string;
  recordedAt: string;
  firstSaved: boolean;
}

const auditCandidatesOf = (
  problems: ProblemListRow[],
  adrs: { edit_user?: string | null; edited?: string; created?: string }[],
): { by: string; at: string; created?: string }[] => {
  const candidates: { by: string; at: string; created?: string }[] = [];

  adrs.forEach((adr) => {
    if (adr && adr.edited && adr.edit_user) {
      candidates.push({ by: String(adr.edit_user), at: String(adr.edited), created: adr.created });
    }
  });

  problems.forEach((row) => {
    if (row && row.edited) {
      candidates.push({
        by: row.edit_user ? String(row.edit_user) : "",
        at: String(row.edited),
        created: row.created,
      });
    }
  });

  return candidates;
};

export const profileAuditOf = (
  problems: ProblemListRow[],
  adrs: { edit_user?: string | null; edited?: string; created?: string }[],
  fallbackUser: string,
): ProfileAudit | null => {
  const candidates = auditCandidatesOf(problems, adrs).filter((c) => c.at);
  if (candidates.length === 0) return null;

  /* เรียงตามเวลาไทย "02/11/2569 [18:51]" และ ISO ได้ทั้งสองแบบ (compare ด้วยความยาวปี พ.ศ.) */
  const sortable = (text: string): string => {
    const be = /^(\d{2})\/(\d{2})\/(\d{4})(?:\s*\[(\d{2}):(\d{2})\])?/.exec(text);
    if (be) return `${be[3]}${be[2]}${be[1]}${be[4] || "00"}${be[5] || "00"}`;
    return text.replace(/[^\d]/g, "");
  };
  candidates.sort((a, b) => sortable(a.at).localeCompare(sortable(b.at)));

  const latest = candidates[candidates.length - 1];
  /* firstSaved จริงเมื่อแถวล่าสุดยังไม่เคยถูกแก้ (created == edited) */
  const firstSaved = sortable(String(latest.created || latest.at)) === sortable(latest.at);

  return {
    recordedBy: latest.by || fallbackUser || "ผู้บันทึก",
    recordedAt: latest.at,
    firstSaved,
  };
};

// ============================================================================
// 3. Vital Sign (สัญญาณชีพ)
// ============================================================================

export interface VitalSignType {
  /** id ของ VitalSignType ในฐานข้อมูล (จำเป็นตอนส่ง vital_signs ให้ API) */
  id?: number;
  /** code ที่ UI ใช้ (BP_SYS ...) ซึ่ง stable กับ values[code] ในการ์ด */
  code: string;
  /** code จริงบน master ของ backend (SP / DP / PR / BT / O2Sat / Height / Weight / BMI / RR) */
  apiCode?: string;
  name: string;
  unit: string;
  min?: number;
  max?: number;
}

/* ============================================================== */
/*   UI code <-> VitalSignType.code ที่ backend ใช้จริง             */
/*   master บน staging (GET /apis/PTM/vital-sign-type/):           */
/*   BMI(1) Weight(3) Height(2) BT(4) SP(5) DP(6) PR(8) O2Sat(1008) RR(1026) */
/* ============================================================== */

const API_CODE_BY_UI_CODE: Record<string, string> = {
  BP_SYS: "SP",
  BP_DIA: "DP",
  PULSE: "PR",
  TEMP: "BT",
  RR: "RR",
  SPO2: "O2Sat",
  HEIGHT: "Height",
  WEIGHT: "Weight",
  BMI: "BMI",
  WAIST: "Waist",
  /* master ใช้ code "PS" สำหรับ Pain Score (id 1025) */
  PAIN_SCORE: "PS",
};

const UI_CODE_BY_API_CODE: Record<string, string> = {
  sp: "BP_SYS",
  systolic: "BP_SYS",
  dp: "BP_DIA",
  diastolic: "BP_DIA",
  pr: "PULSE",
  pulse: "PULSE",
  bt: "TEMP",
  temp: "TEMP",
  "body temperature": "TEMP",
  rr: "RR",
  respiratory: "RR",
  "respiratory rate": "RR",
  o2sat: "SPO2",
  spo2: "SPO2",
  "o2 sat": "SPO2",
  height: "HEIGHT",
  weight: "WEIGHT",
  bmi: "BMI",
  waist: "WAIST",
  pain: "PAIN_SCORE",
  "pain score": "PAIN_SCORE",
  pain_score: "PAIN_SCORE",
  ps: "PAIN_SCORE",
};

export const uiCodeOfApiCode = (code: unknown): string => {
  const raw = String(code ?? "").trim();
  return UI_CODE_BY_API_CODE[raw.toLowerCase()] ?? raw.toUpperCase();
};

export const apiCodeOfUiCode = (code: string): string => API_CODE_BY_UI_CODE[code] ?? code;

/** ListAPIView ทุกตัวตอบ {total,next,previous,items} — ไม่ใช่ results */
export function itemsOf(payload: unknown): any[] {
  if (Array.isArray(payload)) return payload as any[];
  const dict = payload as { items?: unknown; results?: unknown } | null;
  if (Array.isArray(dict?.items)) return dict!.items as any[];
  if (Array.isArray(dict?.results)) return dict!.results as any[];
  return [];
}

/** "2026-11-02" | "02/11/2569" -> "02/11/2569" (ThaiDateField รับ วว/ดด/ปปปป พ.ศ. หรือ YYYY-MM-DD แบบ พ.ศ.) */
/**
 * แปลงค่าวันเวลาของ API ("2026-11-02T18:32:05+07:00" หรือ "02/11/2569 [18:32]")
 * ให้เป็นข้อความ พ.ศ. แบบ "02/11/2569 18:32" สำหรับส่วนหัวของหน้า
 */
export function beDateTimeText(value?: string): string {
  const raw = String(value ?? "").trim();
  if (!raw) return "";

  const thai = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s*\[?\s*(\d{1,2}):(\d{2}))?/.exec(raw);
  if (thai) {
    const [, dd, mm, yyyy, hh, mi] = thai;
    const time = hh ? ` ${hh.padStart(2, "0")}:${mi}` : "";
    return `${dd.padStart(2, "0")}/${mm.padStart(2, "0")}/${yyyy}${time}`;
  }

  const parsed = new Date(raw);
  if (isNaN(parsed.getTime())) return raw;
  const dd = `${parsed.getDate()}`.padStart(2, "0");
  const mm = `${parsed.getMonth() + 1}`.padStart(2, "0");
  const yyyy = `${parsed.getFullYear() + 543}`;
  const hh = `${parsed.getHours()}`.padStart(2, "0");
  const mi = `${parsed.getMinutes()}`.padStart(2, "0");
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
}

export function nowBeDateTimeText(now: Date = new Date()): string {
  const dd = `${now.getDate()}`.padStart(2, "0");
  const mm = `${now.getMonth() + 1}`.padStart(2, "0");
  const yyyy = `${now.getFullYear() + 543}`;
  const hh = `${now.getHours()}`.padStart(2, "0");
  const mi = `${now.getMinutes()}`.padStart(2, "0");
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
}

export function beDateText(value?: string): string {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(raw);
  if (iso) return `${iso[3].padStart(2, "0")}/${iso[2].padStart(2, "0")}/${Number(iso[1]) + 543}`;
  const be = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(raw);
  if (be) return `${be[1].padStart(2, "0")}/${be[2].padStart(2, "0")}/${be[3]}`;
  return raw;
}

/** วันวันนี้แบบ พ.ศ. "วว/ดด/ปปปป" (ThaiDateField ตีความ year = ปีค.ศ. - 543) */
export function todayBeDateText(now: Date = new Date()): string {
  const day = `${now.getDate()}`.padStart(2, "0");
  const month = `${now.getMonth() + 1}`.padStart(2, "0");
  return `${day}/${month}/${now.getFullYear() + 543}`;
}

/** เวลาปัจจุบัน "HH:MM" ตาม format %H:%M ของ serializers.TimeField */
export function nowHourMinuteText(now: Date = new Date()): string {
  return `${`${now.getHours()}`.padStart(2, "0")}:${`${now.getMinutes()}`.padStart(2, "0")}`;
}

/** "9:5" | "09:05:00" | "09:05" -> "09:05" (serializers.TimeField format %H:%M) */
export function hourMinuteText(value?: string): string {
  const raw = String(value ?? "").trim();
  const matched = /^(\d{1,2}):(\d{1,2})/.exec(raw);
  if (!matched) return raw;
  return `${matched[1].padStart(2, "0")}:${matched[2].padStart(2, "0")}`;
}

export const STANDARD_VITAL_SIGN_TYPES: VitalSignType[] = [
  { code: "BP_SYS", apiCode: "SP", name: "Systolic", unit: "mmHg", min: 50, max: 250 },
  { code: "BP_DIA", apiCode: "DP", name: "Diastolic", unit: "mmHg", min: 30, max: 150 },
  { code: "PULSE", apiCode: "PR", name: "Pulse", unit: "-", min: 30, max: 220 },
  { code: "TEMP", apiCode: "BT", name: "Body temperature", unit: "°C", min: 34, max: 43 },
  { code: "RR", apiCode: "RR", name: "RESPIRATORY RATE", unit: "bpm", min: 8, max: 60 },
  { code: "SPO2", apiCode: "O2Sat", name: "O2Sat", unit: "%", min: 50, max: 100 },
  { code: "HEIGHT", apiCode: "Height", name: "ส่วนสูง", unit: "cm.", min: 30, max: 250 },
  { code: "WEIGHT", apiCode: "Weight", name: "น้ำหนัก", unit: "kg.", min: 1, max: 300 },
  { code: "BMI", apiCode: "BMI", name: "BMI", unit: "kg./sq.m" },
  { code: "WAIST", apiCode: "Waist", name: "รอบเอว", unit: "cm.", min: 30, max: 200 },
  { code: "PAIN_SCORE", apiCode: "Pain Score", name: "Pain Score", unit: "-", min: 0, max: 10 },
];

/** GET /apis/PTM/vital-sign-type/ -> VitalSignType[] (code บน master != code ที่ UI ใช้) */
export function vitalSignTypesFromApi(payload: unknown): VitalSignType[] {
  return itemsOf(payload)
    .map((row) => {
      const apiCode = String(row.code ?? row.name ?? "").trim();
      if (!apiCode) return null;
      return {
        id: asNumber(row.id),
        code: uiCodeOfApiCode(apiCode),
        apiCode,
        name: String(row.name ?? apiCode),
        unit: String(row.unit ?? ""),
        min: asNumber(row.min_value ?? row.min),
        max: asNumber(row.max_value ?? row.max),
      } as VitalSignType;
    })
    .filter((row): row is VitalSignType => row !== null);
}

/** GET /apis/PTM/measurement/ -> VitalSignMeasurement[]
    คีย์จริงของ API: id, date ("30/10/2569"), time ("06:19"), edit_user_name,
    vital_signs: [{ vitalsign_type_code, vitalsign_type, result, formatted_string, name }]
    (ไม่มี date_recorded/time_recorded/remark/recorded_by) */
export function vitalSignMeasurementsFromApi(payload: unknown): VitalSignMeasurement[] {
  return itemsOf(payload).map((row) => {
    const values: Record<string, string> = {};
    (Array.isArray(row.vital_signs) ? row.vital_signs : []).forEach((item: any) => {
      const code = uiCodeOfApiCode(item.vitalsign_type_code ?? item.name ?? item.code);
      const raw = item.result ?? item.formatted_string ?? item.value;
      if (code && raw !== undefined && raw !== null && String(raw) !== "") values[code] = String(raw);
    });
    return {
      id: asNumber(row.id) ?? 0,
      dateRecorded: String(row.date ?? ""),
      timeRecorded: String(row.time ?? ""),
      /* remark มาจาก Measurement.remark (PTM) - แถวเก่าที่ backend ยังไม่มี field เป็นค่าว่าง
         -> การ์ดจะไม่ใส่ badge หลอก */
      remark: String(row.remark ?? "").trim() || undefined,
      values,
      recordedBy: String(row.edit_user_name ?? row.edit_user ?? ""),
      encounterId: asNumber(row.encounter),
    } as VitalSignMeasurement;
  });
}

/** payload ของ POST /apis/PTM/measurement/ ตาม MeasurementListSerializer:
    ต้องมี vital_signs (ถ้าไม่มี backend ตอบ 400 "no vital_signs found."),
    date/time เป็น field จริง (ไม่มี date_recorded/time_recorded) และ remark ต้องส่งระดับ Measurement
    (PTM เพิ่ม Measurement.remark ใน branch feature/#86617_measurement_remark)
    และ item ใช้ vitalsign_type = id ของ master + result = ข้อความ */
export function measurementCreatePayload(input: {
  encounterId?: number;
  dateText?: string;
  timeText?: string;
  values: Record<string, string>;
  types: VitalSignType[];
  /** ขั้นตอนที่วัด (แรกรับ / ก่อนทำหัตถการ / ...) -> `Measurement.remark`
      (backend เพิ่ม field ใน PTM feature/#86617_measurement_remark) */
  remark?: string;
}): { encounter: number; date: string; time: string; remark?: string; vital_signs: { vitalsign_type: number; result: string }[] } {
  const wanted = Object.entries(input.values || {}).filter(([, value]) => String(value ?? "").trim() !== "");
  const byUiCode = new Map<string, VitalSignType>();
  (input.types || []).forEach((type) => {
    if (type.id !== undefined) byUiCode.set(type.code, type);
  });

  const vitalsign_type_ids = new Map<number, string>();
  wanted.forEach(([code, value]) => {
    const type = byUiCode.get(code);
    if (type && type.id !== undefined) vitalsign_type_ids.set(type.id, String(value).trim());
  });

  const remark = String(input.remark ?? "").trim();
  return {
    encounter: Number(input.encounterId),
    date: beDateText(input.dateText),
    time: hourMinuteText(input.timeText),
    /* ส่งเฉพาะเมื่อมีค่า เพื่อไม่ส่ง string เปล่าทับข้อมูลเดิม */
    ...(remark ? { remark } : {}),
    vital_signs: Array.from(vitalsign_type_ids.entries()).map(([id, result]) => ({ vitalsign_type: id, result })),
  };
}

export interface VitalSignMeasurement {
  id: number;
  dateRecorded: string; // e.g. "02/11/2569"
  timeRecorded: string; // e.g. "09:30"
  remark?: string; // แรกรับ, ก่อนทำหัตถการ, ระหว่างทำหัตถการ, หลังทำหัตถการ, ก่อนให้ยา / IV, หลังให้ยา / IV, ก่อนกลับบ้าน
  values: Record<string, string>; // e.g. { BP_SYS: "120", BP_DIA: "80", PULSE: "76", TEMP: "36.6", ... }
  recordedBy?: string;
  /** use_patient=true -> แถวมาจากทุก Encounter ของ HN เดียวกัน (issue 86618 ข้อ 8)
      การ์ดแสดงหมายเลข Encounter เมื่อไม่ตรงกับ Encounter ที่กำลังคัดกรองอยู่ */
  encounterId?: number;
}

/** ประวัติการยกเลิกการวัด (audit log ของ issue 86618 ข้อ 6)
    backend เก็บแถวเดิมไว้โดย active=false (MeasurementDetail ยังอ่านได้) — ฝั่ง UI เก็บผู้ลบ/เวลาไว้แสดง */
export interface VitalAuditEntry {
  action: "CANCEL";
  measurementId: number;
  label: string;
  user: string;
  at: string;
}

export interface NewVitalSignInput {
  bpSys: string;
  bpDia: string;
  pulse: string;
  temp: string;
  rr: string;
  spo2: string;
  height: string;
  weight: string;
  bmi: string;
  waist: string;
  painScore: string;
  remark: string;
  customDateTime: boolean;
  /** card เก็บวัน/เวลาระบุเองไว้ที่ manualDate/manualTime แล้วส่งเป็น date/time ตอนบันทึก */
  manualDate?: string;
  manualTime?: string;
  date: string;
  time: string;
}

export const VITAL_REMARK_OPTIONS = [
  "ไม่ระบุ",
  "แรกรับ",
  "ก่อนทำหัตถการ",
  "ระหว่างทำหัตถการ",
  "หลังทำหัตถการ",
  "ก่อนให้ยา / IV",
  "หลังให้ยา / IV",
  "ก่อนกลับบ้าน",
];

/* ============================================================== */
/*   เกณฑ์สีของค่าสัญญาณชีพ (issue 86617 ข้อ 3)                     */
/*   copy เกณฑ์มาจากแบบ 4d PatientWorkspace.dc.html (const judge)  */
/*   level 1 = ผิดปกติ  -> #C98A00                                  */
/*   level 2 = ผิดปกติมาก -> #D9362B                                */
/* ============================================================== */

export const VITAL_COLOR_NORMAL = "#243036";
export const VITAL_COLOR_WARN = "#C98A00";
export const VITAL_COLOR_DANGER = "#D9362B";

export interface VitalSeverity {
  label: string;
  level: 0 | 1 | 2;
  color: string;
}

type VitalJudge = (value: number) => [string, 0 | 1 | 2];

const VITAL_JUDGES: Record<string, VitalJudge> = {
  TEMP: (x) => (x >= 38.5 ? ["สูงมาก", 2] : x >= 37.5 ? ["สูง", 1] : x < 35.5 ? ["ต่ำ", 1] : ["ปกติ", 0]),
  BP_SYS: (x) =>
    x >= 180 ? ["สูงมาก", 2] : x >= 140 ? ["สูง", 1] : x < 80 ? ["ต่ำมาก", 2] : x < 90 ? ["ต่ำ", 1] : ["ปกติ", 0],
  BP_DIA: (x) => (x >= 110 ? ["สูงมาก", 2] : x >= 90 ? ["สูง", 1] : x < 60 ? ["ต่ำ", 1] : ["ปกติ", 0]),
  PULSE: (x) => (x >= 130 ? ["เร็วมาก", 2] : x > 100 ? ["เร็ว", 1] : x < 50 ? ["ช้า", 1] : ["ปกติ", 0]),
  RR: (x) => (x >= 30 ? ["เร็วมาก", 2] : x > 20 ? ["เร็ว", 1] : x < 12 ? ["ช้า", 1] : ["ปกติ", 0]),
  SPO2: (x) => (x < 90 ? ["ต่ำมาก", 2] : x < 95 ? ["ต่ำ", 1] : ["ปกติ", 0]),
  PAIN_SCORE: (x) => (x >= 7 ? ["ปวดมาก", 2] : x >= 4 ? ["ปวดปานกลาง", 1] : ["ปวดน้อย", 0]),
};

/** BMI ใช้เกณฑ์ของ master (min 18.5 / max 25.0) */
const bmiSeverity = (x: number): VitalSeverity => {
  if (x < 18.5 || x >= 25) return { label: x < 18.5 ? "ต่ำ" : "สูง", level: 1, color: VITAL_COLOR_WARN };
  return { label: "ปกติ", level: 0, color: VITAL_COLOR_NORMAL };
};

/** คืน null เมื่อค่าว่าง/ไม่ใช่ตัวเลข (ช่องว่างแสดง "NA") */
export const vitalSeverityOf = (uiCode: string, value?: string | number | null): VitalSeverity | null => {
  const raw = String(value ?? "").trim();
  if (raw === "" || raw === "NA") return null;
  const num = Number(raw);
  if (isNaN(num)) return null;

  if (uiCode === "BMI") return bmiSeverity(num);

  const judge = VITAL_JUDGES[uiCode];
  if (!judge) return { label: "", level: 0, color: VITAL_COLOR_NORMAL };

  const [label, level] = judge(num);
  return {
    label,
    level,
    color: level === 2 ? VITAL_COLOR_DANGER : level === 1 ? VITAL_COLOR_WARN : VITAL_COLOR_NORMAL,
  };
};

/** ค่าบังคับของปุ่ม Save (issue 86617 ข้อ 4.3): อุณหภูมิ Systolic Diastolic ชีพจร Pain score */
export const VITAL_REQUIRED_CODES = ["TEMP", "BP_SYS", "BP_DIA", "PULSE", "PAIN_SCORE"] as const;

const VALUE_OF_CODE: Record<string, keyof NewVitalSignInput> = {
  TEMP: "temp",
  BP_SYS: "bpSys",
  BP_DIA: "bpDia",
  PULSE: "pulse",
  PAIN_SCORE: "painScore",
  RR: "rr",
  SPO2: "spo2",
  HEIGHT: "height",
  WEIGHT: "weight",
  WAIST: "waist",
};

const VITAL_REQUIRED_LABELS: Record<string, string> = {
  TEMP: "อุณหภูมิ",
  BP_SYS: "Systolic",
  BP_DIA: "Diastolic",
  PULSE: "ชีพจร",
  PAIN_SCORE: "Pain score",
  RR: "Respiratory rate",
  SPO2: "O2Sat",
  HEIGHT: "ส่วนสูง",
  WEIGHT: "น้ำหนัก",
  WAIST: "เส้นรอบเอว",
};

/**
 * ค่าบังคับจริงของหน่วยงานที่บันทึกสัญญาณชีพ
 * - master ของหน่วยงานมีไม่เหมือนกัน (backend VitalSignType divisions) เช่นหน่วยงาน MeHealth Medical Lab
 *   มี BMI Height Weight SP DP PR เท่านั้น ไม่มี อุณหภูมิ/Pain score -> ห้ามบังคับค่าที่ไม่มีในช่องกรอก
 * - ไม่มี master (หน่วยงานยังไม่ได้กำหนดชนิดวัด) = ใช้ค่าบังคับครบชุดเหมือนเดิม (ปุ่ม Save จะถูก gate ด้วย
 *   divisionHasVitalTypes อยู่อีกชั้นในการ์ด)
 */
export const requiredVitalCodesFor = (masterCodes?: readonly string[] | null): string[] => {
  if (!masterCodes || masterCodes.length === 0) return [...VITAL_REQUIRED_CODES];
  const present = VITAL_REQUIRED_CODES.filter((code) => masterCodes.indexOf(code) >= 0);
  if (present.length > 0) return [...present];
  /* หน่วยงานที่ไม่มีค่าบังคับชุดหลักเลย: ต้องกรอกอย่างน้อย 1 ค่าที่มีใน master (BMI คำนวณจากน้ำหนัก/ส่วนสูง) */
  return masterCodes.filter((code) => code !== "BMI").slice(0, 1);
};

/** ข้อเตือนบนการ์ด: ชื่อค่าที่ต้องกรอกจริงตามหน่วยงาน */
export const requiredVitalHintFor = (masterCodes?: readonly string[] | null): string =>
  requiredVitalCodesFor(masterCodes)
    .map((code) => VITAL_REQUIRED_LABELS[code] ?? code)
    .join(" ");

export const isNumericText = (text: unknown): boolean => {
  const raw = String(text ?? "").trim();
  return raw !== "" && !isNaN(Number(raw));
};

/** วว/ดด/ปปปป (พ.ศ.) - ช่องว่างไม่รับ */
export const isBeDateTextValid = (text: unknown): boolean =>
  /^(0?[1-9]|[12][0-9]|3[01])\/(0?[1-9]|1[0-2])\/\d{4}$/.test(String(text ?? "").trim());

/** ชช:นน */
export const isHourMinuteValid = (text: unknown): boolean =>
  /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(String(text ?? "").trim());

/** ปุ่ม Save กดได้เมื่อค่าบังคับครบเป็นตัวเลข และรูปแบบวัน/เวลาที่ถูกเลือกถูกต้อง (ข้อ 4.3) */
export const isVitalInputComplete = (
  input?: NewVitalSignInput | null,
  masterCodes?: readonly string[] | null,
): boolean => {
  if (!input) return false;

  const requiredOk = requiredVitalCodesFor(masterCodes).every((code) => {
    const key = VALUE_OF_CODE[code];
    return key !== undefined && isNumericText(input[key]);
  });
  if (!requiredOk) return false;

  if (input.customDateTime) {
    return isBeDateTextValid(input.date) && isHourMinuteValid(input.time);
  }
  return true;
};

// ============================================================================
// 3.1 Body Composition (ผลวิเคราะห์องค์ประกอบร่างกาย)
// ============================================================================

export interface BodyCompositionRecord {
  /** id จาก PTM.BodyComposition ผ่าน GET/POST /apis/PTM/body-composition/ (issue #86758) */
  id?: number;
  dateRecorded: string;
  timeRecorded: string;
  weightKg?: number;
  fatPercent?: number;
  muscleKg?: number;
  waterPercent?: number;
  bmrKcal?: number;
  visceralFat?: number;
  bmi?: number;
  /** ตามชุดตัวชี้วัดของแบบ 4d (BCF): มวลไขมัน (kg), ECW/TBW, InBody Score */
  fatMassKg?: number;
  ecwTbw?: number;
  inBodyScore?: number;
  rawOcrText?: string;
  attachedFileName?: string;
  /** URL ของไฟล์ต้นฉบับ (API source_file_url) - issue #86619 ข้อ 7 "เก็บไฟล์ต้นฉบับไว้ดูย้อนหลังได้" */
  sourceFileUrl?: string;
  ocrModel?: string;
  /** ข้อสังเกตจากการอ่านไฟล์ เช่น เครื่องให้ Body Water เป็นลิตรจึงต้องคำนวณ % */
  ambiguities?: string[];
  recordedBy?: string;
}

/* ============================================================== */
/*   Body Composition API contract (PTM.BodyComposition snake_case)  */
/*   ลำดับตัวชี้วัดต้องตรงกับ BODY_COMP_METRICS ในการ์ด (10 ช่อง)   */
/* ============================================================== */

export const BODY_COMPOSITION_VALUE_FIELDS: { front: keyof BodyCompositionRecord; api: string }[] = [
  { front: "weightKg", api: "weight" },
  { front: "muscleKg", api: "muscle_smm" },
  { front: "fatPercent", api: "fat_percent" },
  { front: "bmi", api: "bmi" },
  { front: "visceralFat", api: "visceral_fat" },
  { front: "bmrKcal", api: "bmr_kcal" },
  { front: "waterPercent", api: "tbw_percent" },
  { front: "fatMassKg", api: "fat_mass" },
  { front: "ecwTbw", api: "ecw_tbw" },
  { front: "inBodyScore", api: "body_score" },
];

/* วัน/เวลา format เดียวกับที่ backend รับ-ตอบ (ThaiDateField "วว/ดด/ปปปป", TimeField "%H:%M")
   ใช้ helper เดิมของหน้าคัดกรอง: isBeDateTextValid / isHourMinuteValid */

export const BODY_COMPOSITION_SAVE_HINT =
  "ตรวจวันที่ (วว/ดด/ปปปป) และน้ำหนักก่อนบันทึก · ถ้าไม่ระบุเวลา จะใช้เวลาปัจจุบัน";

/**
 * เวลานอกใบ (86619 + ผู้ใช้ขอ): PTM.BodyComposition.time ห้ามว่าง (null=false)
 * ใบผลตรวจบางเจ้ามีแต่วันที่เกิดการตรวจ ถ้าการ์ดมีแต่วันเวลา ให้เป็นเวลาปัจจุบัน
 * (ถ้าผู้ใช้พิมพ์เวลาเองผิดรูปแบบ ต้องห้ามส่ง เพราะ backend จะตอบ 400)
 */
export const resolveBcTime = (timeRecorded?: string | null, fallback?: string): string => {
  const time = String(timeRecorded ?? "").trim();
  if (isHourMinuteValid(time)) return time;
  return String(fallback ?? nowHourMinuteText()).trim();
};

/** gate ตาม #86619 ข้อ 5 - คืนข้อความเมื่อไม่พร้อมบันทึก มิฉะนั้นคืน null */
export const bodyCompositionSaveGateErrorOf = (record?: {
  dateRecorded?: string;
  timeRecorded?: string;
  weightKg?: number;
} | null): string | null => {
  if (!record) return BODY_COMPOSITION_SAVE_HINT;
  if (!isBeDateTextValid(record.dateRecorded)) return BODY_COMPOSITION_SAVE_HINT;
  const time = String(record.timeRecorded ?? "").trim();
  if (time !== "" && !isHourMinuteValid(time)) return BODY_COMPOSITION_SAVE_HINT;
  const weight = record.weightKg;
  if (weight === undefined || weight === null || !(Number(weight) > 0)) return BODY_COMPOSITION_SAVE_HINT;
  return null;
};

/** แปลง error จาก /apis/PTM/body-composition* ให้เป็นข้อความที่พยาบาลเข้าใจ */
export const friendlyBodyCompositionErrorText = (text?: string | null): string => {
  const raw = String(text ?? "").trim();
  if (raw === "") return "บันทึกผลองค์ประกอบร่างกายไม่สำเร็จ";
  if (/ALIBABA|OCR|not set/i.test(raw)) {
    return "ระบบอ่านไฟล์ (OCR) ยังไม่ได้ตั้งค่าใน backend - กรอกค่าในตารางด้วยตนเองชั่วคราวได้";
  }
  if (/date|วัน/i.test(raw) && /weight|น้ำหนัก/i.test(raw)) return BODY_COMPOSITION_SAVE_HINT;
  /* DRF JSONField ตอบ "ค่าจะต้องเป็น JSON ที่ถูกต้อง" เมื่อ field array ถูกส่งเป็นข้อความธรรมดา */
  if (/JSON/i.test(raw)) return "บันทึกไม่สำเร็จ: ข้อมูลบางช่องส่งไปไม่ถูกชนิด (ต้องเป็นรายการ JSON)";
  return raw;
};

/** ข้อความบอกเวลารอ OCR (ผู้ใช้ขอไว้ เพราะ backend ใช้เวลา 40-60 วินาที/ไฟล์) */
export const BODY_COMPOSITION_OCR_WAIT_HINT = "ใช้เวลาประมาณ 40-60 วินาที";

export const bodyCompositionSavedMessageOf = (dateRecorded: string, timeRecorded: string): string =>
  `บันทึกผล Body Composition วันที่ ${dateRecorded} ${timeRecorded} แล้ว`;

/**
 * ชื่อไฟล์จาก path หรือ signed URL (GCS) - ต้องตัด ?query ออก
 * ไม่งั้นได้ "MC-980.jpg?X-Goog-Algorithm=..." ซึ่งแสดงในตารางไม่ได้
 */
const fileNameFromSource = (value?: string | null): string | undefined => {
  const raw = String(value ?? "").trim();
  if (raw === "") return undefined;
  const name = raw.split("?")[0].split("#")[0].split("/").pop();
  if (!name) return undefined;
  try {
    return decodeURIComponent(name);
  } catch (decodeError) {
    return name;
  }
};

export const bodyCompositionFromApi = (row: any): BodyCompositionRecord | null => {
  if (!row) return null;
  const record: BodyCompositionRecord = {
    id: asNumber(row.id),
    dateRecorded: String(row.date ?? "").trim(),
    timeRecorded: String(row.time ?? "").trim(),
    rawOcrText: row.raw_ocr_text ? String(row.raw_ocr_text) : undefined,
    sourceFileUrl: row.source_file_url ? String(row.source_file_url) : undefined,
    attachedFileName: fileNameFromSource(row.source_file_url || row.source_file),
    ocrModel: row.ocr_model ? String(row.ocr_model) : undefined,
    ambiguities: Array.isArray(row.ocr_ambiguities) ? row.ocr_ambiguities.map((item: any) => String(item)) : [],
    recordedBy: row.edit_user_name ? String(row.edit_user_name) : undefined,
  };
  BODY_COMPOSITION_VALUE_FIELDS.forEach(({ front, api }) => {
    const value = asNumber(row[api]);
    if (value !== undefined) (record as any)[front] = value;
  });
  return record;
};

export const bodyCompositionCreatePayload = (
  record: Partial<BodyCompositionRecord>,
  encounterId?: number | null,
): Record<string, any> => {
  const payload: Record<string, any> = {
    encounter: encounterId ?? null,
    /* ต้องส่ง active=true ทุกครั้ง: DRF อ่าน BooleanField จาก multipart/form-data
       ว่า false เมื่อ key หายไป (ทดสอบจริง: POST แบบ FormData ได้แถว active=false -> หายจาก list
       เพราะ queryset กรอง active=True -> ข้อมูลที่เพิ่งบันทึกไม่กลับมาหลังเปลี่ยน tab) */
    active: true,
    date: String(record.dateRecorded ?? "").trim(),
    /* ต้องเป็น HH:MM เสมอ: ใบที่ไม่มีเวลา ให้เป็นเวลาตอนที่บันทึก */
    time: resolveBcTime(record.timeRecorded),
    raw_ocr_text: record.rawOcrText ?? "",
    ocr_model: record.ocrModel ?? "",
    /* JSONField ของ backend ต้องการ list เท่านั้น (ดู appendFormValue ใน ScreenV4: FormData ต้องเป็น JSON string) */
    ocr_ambiguities: Array.isArray(record.ambiguities)
      ? record.ambiguities
      : String(record.ambiguities ?? "").trim() === ""
        ? []
        : [String(record.ambiguities)],
  };
  BODY_COMPOSITION_VALUE_FIELDS.forEach(({ front, api }) => {
    const value = (record as any)[front];
    payload[api] = value === undefined || value === null || String(value).trim() === "" ? null : value;
  });
  return payload;
};

export interface BodyCompositionOcrResult {
  rawText: string;
  values: Partial<BodyCompositionRecord>;
  date?: string;
  time?: string;
  instrument?: string;
  ambiguities: string[];
  ocrModel?: string;
  extractModel?: string;
}

/** POST /apis/PTM/body-composition-ocr/ -> {raw_text, values{snake_case}, date, time, instrument, ambiguities} */
export const bodyCompositionOcrFromApi = (data: any): BodyCompositionOcrResult | null => {
  if (!data) return null;
  const apiValues = data.values || {};
  const values: Partial<BodyCompositionRecord> = {};
  BODY_COMPOSITION_VALUE_FIELDS.forEach(({ front, api }) => {
    const value = asNumber(apiValues[api]);
    if (value !== undefined) (values as any)[front] = value;
  });
  return {
    rawText: String(data.raw_text ?? ""),
    values,
    date: data.date ? String(data.date) : undefined,
    time: data.time ? String(data.time) : undefined,
    instrument: data.instrument ? String(data.instrument) : undefined,
    ambiguities: Array.isArray(data.ambiguities) ? data.ambiguities.map((item: any) => String(item)) : [],
    ocrModel: data.ocr_model ? String(data.ocr_model) : undefined,
    extractModel: data.extract_model ? String(data.extract_model) : undefined,
  };
}

// ============================================================================
// State & Sequence Slice
// ============================================================================

export interface ScreenIds {
  encounterId?: number;
  patientId?: number;
  patientHn?: string;
  doctorId?: number;
  emrId?: number;
  divisionId?: number;
}

export interface ScreenRunParams {
  sequence: string;
  action: string;
  card?: string;
  restart?: boolean;
  encounterId?: number;
  patientId?: number;
  patientHn?: string;
  doctorId?: number;
  /** id ของ AdverseReaction ที่ผู้ใช้กด "ลบ" ในการดร์ (sequence จะส่ง action CANCEL) */
  removedAllergyIds?: number[];
  [key: string]: unknown;
}

export type ScreenRunSequenceProp = (params: ScreenRunParams) => void;

/**
 * Slice ของ state ใน sequence ScreenV4
 * กฎสถาปัตยกรรม: ห้ามมี field loading / saving / error ใน slice
 */
/** ClinicalTerm (master) ที่ triage ต้องอ้างอิง (case = type "PC", arrive_status = type "PAB") */
export interface ClinicalTermOption {
  id: number;
  code?: string;
  name: string;
  isDefault?: boolean;
}

export function clinicalTermOptionsFromApi(payload: unknown): ClinicalTermOption[] {
  return itemsOf(payload)
    .map((row) => ({
      id: asNumber(row.id) ?? asNumber(row.pk) ?? 0,
      name: String(row.name ?? ""),
      isDefault: Boolean(row.is_default),
      code: row.code === undefined ? undefined : String(row.code),
    }))
    .filter((row) => row.id > 0);
}

/** เลือก ClinicalTerm ที่ master ตั้งเป็น default (ห้าม hardcode id ข้าม environment) */
export function defaultClinicalTermId(options: ClinicalTermOption[] | undefined): number | undefined {
  const list = options || [];
  const fallback = list.find((option) => option.isDefault) || list[0];
  return fallback ? fallback.id : undefined;
}

export interface ScreenV4Slice {
  triage: TriageData;
  healthProfile: HealthProfileData;
  problemRows?: ProblemListRow[];
  problemRowsUpdatedAt?: string;
  panelUpdatedAt?: string;
  vitalSignTypes: VitalSignType[];
  vitalSignMeasurements: VitalSignMeasurement[];
  bodyCompositionList: BodyCompositionRecord[];
  /** ผล OCR/latest ที่ยัง "ไม่ถูกบันทึก" - ใช้เติมคอลัมน์ "ค่าใหม่" ของตาราง */
  bodyCompositionOcr?: BodyCompositionOcrResult | null;
  /** ข้อความหลังบันทึกสำเร็จ: "บันทึกผล Body Composition วันที่ ... แล้ว" (#86619 ข้อ 7) */
  bodyCompositionSavedMessage?: string;
  latestHeight?: number;
  sentToQueue: boolean;
  sentEncounterStatus?: string;
  /** master ของ triage (case / arrive_status) จาก /apis/core/clinicalterm/?type=PC|PAB */
  triageCaseOptions?: ClinicalTermOption[];
  arriveStatusOptions?: ClinicalTermOption[];
  /** ส่วนหัวของหน้า: "บันทึกล่าสุด {เวลา} โดย {ชื่อ}" หรือ "ยังไม่ได้บันทึก" (issue 86613 ข้อ 3) */
  savedAt?: string;
  savedBy?: string;
  /** เวลาของไทยที่บันทึกสัญญาณชีพล่าสุด ใช้แสดง "บันทึก Vital Sign แล้ว {วันเวลา}" (ข้อ 5) */
  vitalSavedAt?: string;
  /** สถานะจริงของ Encounter จาก API (core Encounter.status) - ใช้ตัดสินใจว่า "ส่งเข้าคิวแล้ว" */
  encounterStatus?: number;
  encounterStatusName?: string;
  /** pre-check ของการสร้างการคัดกรอง (issue 86747) + ข้อความอธิบายผู้ใช้ */
  triageEligibility?: TriageEligibility;
  triageNotice?: string;
  assessmentWarning?: string;
  /** ประวัติการยกเลิกการวัดล่าสุด (issue 86618 ข้อ 6) */
  vitalAudit?: VitalAuditEntry[];
  /** ส่วนที่โหลดไม่สำเร็จ (ชื่อ key) — ใช้แสดงใน UI ว่าสวนไหนข้อมูลไม่มา */
  partialErrors?: string[];
  patientHn?: string;
  encounterId?: number;
  patientId?: number;
  doctorId?: number;
  sequenceIndex?: string;
}

/** chip นำทางภายในหน้าคัดกรอง (issue 86613 ข้อ 4 มี 3 อัน ตามแบบ 4d) */
export const SECTION_NAV: { id: string; key: "triage" | "profile" | "vital"; label: string }[] = [
  { id: "sec-triage", key: "triage", label: "1 · อาการนำ" },
  { id: "sec-profile", key: "profile", label: "2 · ประวัติสุขภาพ" },
  { id: "sec-vital", key: "vital", label: "3 · Vital Sign" },
];

// ============================================================================
// Pure Functions & Computations
// ============================================================================

/** คำนวณ BMI อัตโนมัติ: weight (kg) / [height (m)]^2 */
export function calculateBmi(weightKgStr?: string | number, heightCmStr?: string | number): string {
  const w = typeof weightKgStr === "number" ? weightKgStr : parseFloat(String(weightKgStr || "").trim());
  const h = typeof heightCmStr === "number" ? heightCmStr : parseFloat(String(heightCmStr || "").trim());

  if (!Number.isFinite(w) || w <= 0 || !Number.isFinite(h) || h <= 0) {
    return "";
  }

  const heightM = h / 100;
  const bmiVal = w / (heightM * heightM);
  return Number.isFinite(bmiVal) ? bmiVal.toFixed(1) : "";
}

export interface BmiCategory {
  category: "น้ำหนักน้อย" | "ปกติ" | "ท้วม" | "อ้วน" | "อ้วนอันตราย";
  color: string;
}

export function getBmiCategory(bmi: number): BmiCategory {
  if (bmi < 18.5) return { category: "น้ำหนักน้อย", color: "#1B7FCC" };
  if (bmi < 23) return { category: "ปกติ", color: "#1E8E3E" };
  if (bmi < 25) return { category: "ท้วม", color: "#F2994A" };
  if (bmi < 30) return { category: "อ้วน", color: "#E37400" };
  return { category: "อ้วนอันตราย", color: "#D93025" };
}

/** ตรวจสอบความครบถ้วนก่อนส่งเข้าคิวตรวจ */
/** สถานะ Encounter ที่ "ส่งเข้าคิวรอตรวจแล้ว" (core.models.Encounter.STATUS)
    WAIT_TRIAGE 1 · ARRIVING 2 · ARRIVED 3 · SCREENED 4  = ยังไม่ส่ง
    IN_QUEUE 5 · IN_EXAM 6 · CHECKED_OUT 10 · WAIT_PAY 11 · WAIT_DISPENSE 12 · DISCHARGED 13
    · WAIT_RESULT 15 · RECEIVE_RESULT 16                       = ส่งแล้ว                                   */
export const ENCOUNTER_STATUS_QUEUED_OR_LATER = [5, 6, 10, 11, 12, 13, 15, 16];

/** `OPD_SCREEN` (ซักประวัติแล้ว) ใช้ได้เฉพาะ WAIT_TRIAGE/ARRIVING/ARRIVED เท่านั้น
    (TRANSITION ใน core/models.py:5330,5337,5352) และ `OPD_QUEUE_EXAM` ใช้ต่อจาก SCREENED
    (core/models.py:5347) -> การ "ส่งผู้ป่วยเข้าคิวรอตรวจ" จากหน้าคัดกรองต้อง ทำ 2 สเตป */
export const ENCOUNTER_STATUS_NEEDS_SCREEN_ACTION = [1, 2, 3];

export const isEncounterAlreadyQueued = (status: unknown): boolean => {
  const num = asNumber(status);
  return num !== undefined && ENCOUNTER_STATUS_QUEUED_OR_LATER.includes(num);
};

export function validateScreening(data: {
  chiefComplaint: string;
  healthProfile: HealthProfileData;
  /** issue 86620 ข้อ 2: ถ้ากำลังแก้ประวัติสุขภาพ ต้อง "กดบันทึกประวัติสุขภาพ" ก่อน */
  profileEditMode?: boolean;
  /** จำนวนการวัดสัญญาณชีพที่บันทึกแล้วใน Encounter นี้ */
  vitalSavedCount?: number;
  /** กรอกค่าบังคับของสัญญาณชีพครบแล้ว (บันทึกได้) */
  vitalInputComplete?: boolean;
}): { valid: boolean; missingFields: string[] } {
  const missing: string[] = [];

  if (!data.chiefComplaint || data.chiefComplaint.trim() === "") {
    missing.push("อาการนำ (Chief complaint)");
  }

  if (data.profileEditMode) {
    missing.push("กดบันทึกประวัติสุขภาพ");
  } else {
    const hasAllergyStatus = Boolean(data.healthProfile.nkda) || data.healthProfile.allergies.length > 0;
    if (!hasAllergyStatus) {
      missing.push("ประวัติแพ้ (ระบุรายการแพ้ หรือกดปฏิเสธการแพ้)");
    }
    if (!isHealthProfileComplete(data.healthProfile)) {
      missing.push("ประวัติสุขภาพ (โรคประจำตัว ยาที่ใช้ประจำ ประวัติผ่าตัด)");
    }
  }

  const saved = data.vitalSavedCount ?? 0;
  if (saved === 0 && data.vitalInputComplete !== true) {
    missing.push("สัญญาณชีพ (อุณหภูมิ ความดัน ชีพจร Pain)");
  }

  return {
    valid: missing.length === 0,
    missingFields: missing,
  };
}

// ============================================================================
// Mock Data (สำหรับ Preview / Fallback)
// ============================================================================

export const MOCK_TRIAGE: TriageData = {
  id: 101,
  chiefComplaint: "มีไข้ ไอ เจ็บคอ 2 วันก่อนมา รพ.",
  triageLevel: "4",
  arriveStatus: "WALK",
};

export const MOCK_HEALTH_PROFILE: HealthProfileData = {
  underlyingDiseases: "ความดันโลหิตสูง (HT), ไขมันในเลือดสูง (DLP)",
  surgicalHistory: "ผ่าตัดไส้ติ่งอักเสบ (ปี 2558)",
  currentMedications: "Amlodipine (5mg) 1 tab po od pc, Simvastatin (20mg) 1 tab po hs",
  smoking: "ไม่สูบ",
  alcohol: "ดื่มเป็นครั้งคราว (สังคม)",
  nkda: false,
  allergies: [
    { id: 1, type: "ยา", name: "Amoxicillin", react: "ผื่นคัน ลมพิษ" },
    { id: 2, type: "อาหาร", name: "กุ้ง", react: "ตาบวม คันคอ" },
  ],
  recordedBy: "พว. พัชรี แสนสุข (RN)",
  recordedAt: "02/11/2569 08:45",
};

/** master ของหน่วยงาน (shape เดียวกับ GET /apis/PTM/vital-sign-type/?div_from_encounter=1) */
export const MOCK_VITAL_SIGN_TYPES: VitalSignType[] = [
  { id: 1, code: "BMI", apiCode: "BMI", name: "BMI", unit: "kg./sq.m", min: 18.5, max: 25 },
  { id: 3, code: "WEIGHT", apiCode: "Weight", name: "น้ำหนัก", unit: "kg." },
  { id: 2, code: "HEIGHT", apiCode: "Height", name: "ส่วนสูง", unit: "cm." },
  { id: 4, code: "TEMP", apiCode: "BT", name: "Body temperature", unit: "°C", min: 36, max: 38 },
  { id: 5, code: "BP_SYS", apiCode: "SP", name: "Systolic", unit: "mmHg", min: 119, max: 130 },
  { id: 6, code: "BP_DIA", apiCode: "DP", name: "Diastolic", unit: "mmHg", min: 79, max: 85 },
  { id: 8, code: "PULSE", apiCode: "PR", name: "Pulse", unit: "-", min: 39, max: 131 },
  { id: 1008, code: "SPO2", apiCode: "O2Sat", name: "O2Sat", unit: "%", min: 95 },
  { id: 1025, code: "PAIN_SCORE", apiCode: "PS", name: "Pain Score", unit: "-", min: 1, max: 10 },
  { id: 1026, code: "RR", apiCode: "RR", name: "Respiratory rate", unit: "ครั้ง/นาที" },
];

export const MOCK_VITAL_SIGNS: VitalSignMeasurement[] = [
  {
    id: 1001,
    dateRecorded: "02/11/2569",
    timeRecorded: "08:50",
    remark: "แรกรับ",
    values: {
      BP_SYS: "128",
      BP_DIA: "82",
      PULSE: "84",
      TEMP: "37.8",
      RR: "18",
      SPO2: "98",
      HEIGHT: "165",
      WEIGHT: "62.5",
      BMI: "23.0",
      WAIST: "78",
      PAIN_SCORE: "2",
    },
    recordedBy: "พว. สมพร เจริญ",
  },
  {
    id: 1002,
    dateRecorded: "02/11/2569",
    timeRecorded: "09:30",
    remark: "ก่อนทำหัตถการ",
    values: {
      BP_SYS: "122",
      BP_DIA: "78",
      PULSE: "78",
      TEMP: "37.2",
      RR: "16",
      SPO2: "99",
      HEIGHT: "165",
      WEIGHT: "62.5",
      BMI: "23.0",
      WAIST: "78",
      PAIN_SCORE: "1",
    },
    recordedBy: "พว. สมพร เจริญ",
  },
];

export const MOCK_BODY_COMPOSITION: BodyCompositionRecord[] = [
  {
    id: 501,
    dateRecorded: "02/11/2569",
    timeRecorded: "08:55",
    weightKg: 62.5,
    fatPercent: 22.4,
    muscleKg: 46.2,
    waterPercent: 55.8,
    bmrKcal: 1420,
    visceralFat: 5,
    bmi: 23.0,
    fatMassKg: 14.2,
    ecwTbw: 0.36,
    inBodyScore: 73,
    attachedFileName: "inbody_scan_02112569.pdf",
  },
];

/** ผล OCR ตัวอย่างของการ์ด 3.1 (ตรงคอลัมน์ "ค่าใหม่") - โครงเดียวกับที่ GET/POST
    /apis/PTM/body-composition-ocr/ ตอบ: raw_text, values, date, time, instrument, ambiguities */
export const MOCK_BODY_COMPOSITION_OCR: BodyCompositionOcrResult = {
  rawText:
    "# InBody 570\n" +
    "Weight 62.5 kg   Body Fat Percent 22.4%   Skeletal Muscle Mass 46.2 kg\n" +
    "Total Body Water 55.8%   BMR 1420 kcal   Visceral Fat 5   BMI 23.0\n" +
    "Body Fat Mass 14.2 kg   ECW/TBW 0.36   InBody Score 73\n",
  values: {
    weightKg: 62.5,
    muscleKg: 46.2,
    fatPercent: 22.4,
    bmi: 23.0,
    visceralFat: 5,
    bmrKcal: 1420,
    waterPercent: 55.8,
    fatMassKg: 14.2,
    ecwTbw: 0.36,
    inBodyScore: 73,
  },
  date: "02/11/2569",
  time: "08:55",
  instrument: "InBody 570",
  ambiguities: [],
  ocrModel: "qwen3-vl-plus",
  extractModel: "qwen-flash",
};

export const EMPTY_TRIAGE: TriageData = {
  chiefComplaint: "",
  triageLevel: undefined,
};

export const EMPTY_HEALTH_PROFILE: HealthProfileData = {
  underlyingDiseases: "ไม่มี",
  surgicalHistory: "ไม่มี",
  currentMedications: "ไม่มี",
  smoking: "ไม่สูบ",
  alcohol: "ไม่ดื่ม",
  nkda: true,
  allergies: [],
};

export const INITIAL_SCREEN_SLICE: ScreenV4Slice = {
  triage: EMPTY_TRIAGE,
  healthProfile: EMPTY_HEALTH_PROFILE,
  vitalSignTypes: STANDARD_VITAL_SIGN_TYPES,
  vitalSignMeasurements: [],
  bodyCompositionList: [],
  bodyCompositionOcr: null,
  bodyCompositionSavedMessage: "",
  latestHeight: undefined,
  sentToQueue: false,
  sequenceIndex: "Action",
};

